// Small formatting helpers shared across the app. Locale-aware where relevant.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Loose email shape check shared by the auth forms. */
export function isValidEmail(email: string): boolean {
  return EMAIL_RE.test(email);
}

const AVATAR_COLORS = [
  "#e11d48",
  "#7c3aed",
  "#ea580c",
  "#0284c7",
  "#64748b",
  "#0d9488",
  "#4f46e5",
] as const;

/** Deterministic avatar background for a seed (name/email/id). */
export function avatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/** 1–2 letter initials from a name, falling back to an email local-part. */
export function initials(name?: string | null, email?: string | null): string {
  const source = (name || email || "?").trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Render a small, safe subset of Markdown to HTML (mirrors widget/src/format.ts).
// Input is untrusted — everything is escaped first, then `code`, **bold**,
// *italic*, and [label](http…) links are reintroduced. Newlines are preserved
// by the caller's `whitespace-pre-wrap`.
export function renderMarkdown(raw: string): string {
  if (!raw) return "";
  const codes: string[] = [];
  let text = raw.replace(/`([^`]+)`/g, (_m, code: string) => {
    codes.push(code);
    return ` C${codes.length - 1} `;
  });
  text = escapeHtml(text);
  text = text.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_m, label: string, url: string) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`,
  );
  text = text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  text = text.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  text = text.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");
  text = text.replace(/(^|[^_\w])_([^_\n]+)_(?![_\w])/g, "$1<em>$2</em>");
  text = text.replace(/ C(\d+) /g, (_m, i: string) => `<code>${escapeHtml(codes[Number(i)])}</code>`);
  return text;
}

/** True for "de", "de-DE", "de-AT", … (the detector emits regional tags). */
function isGerman(locale: string): boolean {
  return locale.toLowerCase().startsWith("de");
}

/** Compact relative time ("2m", "1h", "3d") — falls back to a localized date. */
export function relativeTime(input: string | number | Date, locale = "de"): string {
  const date = input instanceof Date ? input : new Date(input);
  const diffMs = Date.now() - date.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return isGerman(locale) ? "jetzt" : "now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d`;
  return date.toLocaleDateString(locale, { day: "numeric", month: "short" });
}

/** Long-form localized date+time (used in inspector / detail headers). */
export function formatDateTime(input: string | number | Date, locale = "de"): string {
  const date = input instanceof Date ? input : new Date(input);
  return date.toLocaleString(locale, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Day separator label for a message thread. */
export function dayLabel(input: string | number | Date, locale = "de"): string {
  const date = input instanceof Date ? input : new Date(input);
  return date.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });
}
